import type {
  CustomFieldDefinition,
  CustomFieldValue,
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { match } from 'ts-pattern';
import { getFeatureFlagLabel } from '~/utils/clientFeatureFlagUtils';
import { getClientEditLink, getContractPlanLink } from '~/utils/getLinks';
import type { ClientDetailPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof ClientDetailPresentation>;

// ────────────────────────────────────────
// プライベートヘルパー関数
// ────────────────────────────────────────

/**
 * カスタムフィールドの表示値を取得する
 *
 * カスタムフィールドの入力タイプ（input_type）および値タイプ（value_type）に応じて、
 * 適切な表示用の文字列に変換する。
 *
 * @param field - カスタムフィールド定義
 * @param value - カスタムフィールドの値（カンマ区切りの場合あり）
 * @returns 表示用に変換された文字列
 */
const convertCustomFieldValueToLabel = (field: CustomFieldDefinition, value?: string): string => {
  if (!value) return '-';

  return match(field)
    .returnType<string>()
    .with({ input_type: 'choice' }, field => field.options?.[value]?.toString() ?? value)
    .with({ input_type: 'multi_choice' }, field =>
      value
        .split(',')
        .map(v => v.trim())
        .map(item => field.options?.[item] ?? item)
        .join(', ')
    )
    .with({ value_type: 'bool' }, () =>
      match(value)
        .with('true', () => 'はい')
        .with('false', () => 'いいえ')
        .otherwise(() => value)
    )
    .otherwise(() => value);
};

/**
 * convertToPresentationProps の引数オブジェクト
 */
type Args = {
  /** クライアント詳細 API のレスポンスデータ */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200;
  /** カスタムフィールド定義一覧 */
  customFieldDefinitions: GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200;
  /** パートナーフィーチャーフラグ一覧 */
  partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * カスタムフィールド値をラベルでグルーピングする
 *
 * 同じラベルを持つ複数のカスタムフィールド値（複数選択の場合など）を
 * カンマ区切りの文字列として1つにまとめる。
 *
 * @param customFieldValues - カスタムフィールド値の配列
 * @returns ラベルをキー、値（カンマ区切り）をバリューとするMap
 */
const groupCustomFieldValuesByLabel = (
  customFieldValues: CustomFieldValue[]
): Map<string, string> => {
  const grouped = new Map<string, string>();

  for (const { label, value } of customFieldValues) {
    const existing = grouped.get(label);
    grouped.set(label, existing ? `${existing},${value.toString()}` : value.toString());
  }

  return grouped;
};

/**
 * カスタムフィールドを表示用の配列に変換する
 *
 * @param customFieldDefinitions - カスタムフィールド定義の配列
 * @param customFieldValues - カスタムフィールド値の配列（nullの場合あり）
 * @returns 表示用のカスタムフィールド配列
 */
const convertCustomFields = (
  customFieldDefinitions: CustomFieldDefinition[],
  customFieldValues: CustomFieldValue[] | null | undefined
): PresentationProps['customFields'] => {
  if (customFieldDefinitions.length === 0) {
    return [];
  }
  const groupedValues = groupCustomFieldValuesByLabel(customFieldValues ?? []);

  return customFieldDefinitions.map(field => ({
    id: field.id,
    label: field.label,
    value: convertCustomFieldValueToLabel(field, groupedValues.get(field.label)),
  }));
};

/**
 * APIレスポンスをPresentationコンポーネントのpropsに変換する
 *
 * @param args.data - クライアント詳細APIのレスポンスデータ
 * @param args.customFieldDefinitions - カスタムフィールド定義一覧
 * @param args.partnerFeatureFlags - パートナーフィーチャーフラグ一覧
 * @returns Presentation用に整形されたprops
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  customFieldDefinitions,
  partnerFeatureFlags,
}) => {
  const { client } = data;

  return {
    clientName: client.name,
    editLink: getClientEditLink(String(client.id)),
    // 会社情報
    companyNameKana: client.name_kana || '-',
    representativeName: client.representative_name || '-',
    corporateNumber: client.corporate_number || '-',
    externalClientId: client.external_client_id || '-',
    groupName: client.groups?.map(g => g.name).join(', ') || '-',
    address: {
      postalCode: client.postal_code
        ? `〒${client.postal_code.slice(0, 3)}-${client.postal_code.slice(3)}`
        : '-',
      prefectureCity:
        client.prefecture || client.city
          ? `${client.prefecture?.name ?? ''}${client.city ?? ''}`
          : '-',
      streetAddress: client.street_address || '-',
    },
    employeeCount: client.employee_count != null ? `${client.employee_count}人` : '-',
    partnerBranchName: client.partner_branch?.name || '-',
    userCount: client.user_count != null ? `${client.user_count}人` : '-',
    // non_invited_user_count（招待ユーザーを除いた有効なユーザーの数）を課金対象者数として使用
    nonInvitedUserCount:
      client.non_invited_user_count != null ? `${client.non_invited_user_count}人` : '-',
    // PEN-986: 移行期間中は「取引店舗」カスタムフィールドを非表示にする（定義削除後に PEN-987 で撤去）
    customFields: convertCustomFields(
      customFieldDefinitions.custom_field_definitions.filter(
        def => def.target_model === 'client' && def.label !== '取引店舗'
      ),
      client.custom_field_values
    ),
    // プラン情報（機能フラグを統合）
    contractPlanName: client.contract_plan?.name ?? '-',
    contractPlanLink: client.contract_plan ? getContractPlanLink(client.contract_plan.id) : '',
    desiredStartMonth: client.desired_start_month
      ? client.desired_start_month.replace('-', '/')
      : '-',
    clientFeatureFlags: client.client_feature_flags
      ?.filter(flag =>
        partnerFeatureFlags.partner_feature_flags.some(
          pf => pf.feature_key === flag.feature_key && pf.enabled
        )
      )
      .map(flag => ({
        featureKey: flag.feature_key,
        isEnabled: flag.enabled,
        label: getFeatureFlagLabel(flag.feature_key),
      })),
    // 担当者情報
    contactPersonName: client.contact_person_name || '-',
    contactPersonNameKana: client.contact_person_name_kana || '-',
    contactPersonEmail: client.contact_person_email || '-',
    contactPersonPhone: client.contact_person_phone || '-',
    contactPreferredTime: client.contact_preferred_time || '-',
    // 請求先情報
    billingContactName: client.billing_contact_name || '-',
    billingContactNameKana: client.billing_contact_name_kana || '-',
    billingEmail: client.billing_email || '-',
    // 作成・更新日時
    createdAt: client.created_at
      ? formatDate({ date: new Date(client.created_at * 1000), formatType: 'yyyy/MM/dd H:mm' })
      : '-',
    updatedAt: client.updated_at
      ? formatDate({ date: new Date(client.updated_at * 1000), formatType: 'yyyy/MM/dd H:mm' })
      : '-',
  };
};
